import { useToast } from '@chakra-ui/react';
import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import type {
  AppleTokenRequest,
  AuthOtpRequestRequest,
  AuthOtpRequestResult,
  AuthOtpValidationRequest,
  GoogleTokenRequest,
  IErrorApiModel,
  JwtDto,
  LoginByEmailRequest,
  OutlookAccessTokenRequest,
  OutlookTokenRequest,
  PutIdentityNumberRequest,
  SsoStartRequest,
  SsoStartRequestResponse,
  SsoVerifyRequest,
} from '@/services/api';
import { AuthService } from '@/services/api';

export type SsoStartResponse =
  | string
  | {
      redirectUrl?: string;
      url?: string;
      [key: string]: unknown;
    };

export const useLoginMutation = (
  options?: UseMutationOptions<
    JwtDto,
    AxiosError<IErrorApiModel>,
    LoginByEmailRequest
  >
) => {
  return useMutation<JwtDto, AxiosError<IErrorApiModel>, LoginByEmailRequest>({
    mutationFn: (body) => {
      return AuthService.postAuthLogin({
        body,
      });
    },
    ...options,
  });
};

export const useSSOLoginMutation = (
  options?: UseMutationOptions<
    SsoStartRequestResponse,
    AxiosError<IErrorApiModel>,
    SsoStartRequest
  >
) => {
  return useMutation<
    SsoStartRequestResponse,
    AxiosError<IErrorApiModel>,
    SsoStartRequest
  >({
    mutationFn: (body) => {
      return AuthService.postAuthSsoStart({
        body,
      });
    },
    ...options,
  });
};

export const useSSOLoginVerifyMutation = (
  options?: UseMutationOptions<
    JwtDto,
    AxiosError<IErrorApiModel>,
    SsoVerifyRequest
  >
) => {
  return useMutation<JwtDto, AxiosError<IErrorApiModel>, SsoVerifyRequest>({
    mutationFn: (body) => {
      return AuthService.postAuthSsoVerify({
        body,
      });
    },
    ...options,
  });
};

export const useGoogleAuthValidateMutation = (
  options?: UseMutationOptions<
    JwtDto,
    AxiosError<IErrorApiModel>,
    GoogleTokenRequest
  >
) => {
  return useMutation<JwtDto, AxiosError<IErrorApiModel>, GoogleTokenRequest>({
    mutationFn: (body) => {
      return AuthService.postAuthExternalGoogleValidate({
        body,
      });
    },
    ...options,
  });
};

export const useAppleAuthValidateMutation = (
  options?: UseMutationOptions<
    JwtDto,
    AxiosError<IErrorApiModel>,
    AppleTokenRequest
  >
) => {
  return useMutation<JwtDto, AxiosError<IErrorApiModel>, AppleTokenRequest>({
    mutationFn: (body) => {
      return AuthService.postAuthExternalAppleValidate({
        body,
      });
    },
    ...options,
  });
};

export const usePostAuthOtpRequestMutation = (
  options?: UseMutationOptions<
    AuthOtpRequestResult,
    AxiosError,
    AuthOtpRequestRequest
  >
) => {
  return useMutation<AuthOtpRequestResult, AxiosError, AuthOtpRequestRequest>({
    mutationFn: async (payload) => {
      return AuthService.postAuthOtpRequest({ body: payload });
    },
    ...options,
    async onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const usePostAuthOtpValidateMutation = (
  options?: UseMutationOptions<JwtDto, AxiosError, AuthOtpValidationRequest>
) => {
  const toast = useToast();
  return useMutation<JwtDto, AxiosError, AuthOtpValidationRequest>({
    mutationFn: async (payload) => {
      return AuthService.postAuthOtpValidate({ body: payload });
    },
    ...options,
    async onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    onError(err) {
      if (err.response?.status === 400) {
        const data = err.response?.data as IErrorApiModel;
        toast({
          title: data.detail ?? '',
          status: 'error',
        });
      }
    },
  });
};

export const useAuthMeIdentityNumberMutation = (
  options?: UseMutationOptions<unknown, AxiosError, PutIdentityNumberRequest>
) => {
  const toast = useToast();
  const { t } = useTranslation(['components']);

  return useMutation<unknown, AxiosError, PutIdentityNumberRequest>({
    mutationFn: (body) => {
      return AuthService.putAuthMeIdentityNumber({
        body,
      });
    },
    ...options,
    onSuccess(data, variables, context) {
      toast({
        title: t('components:idNumber.submitApiSuccess'),
        status: 'success',
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useOutlookAuthValidateMutation = (
  options?: UseMutationOptions<
    JwtDto,
    AxiosError<IErrorApiModel>,
    OutlookTokenRequest
  >
) => {
  return useMutation<JwtDto, AxiosError<IErrorApiModel>, OutlookTokenRequest>({
    mutationFn: (body) => {
      return AuthService.postAuthExternalOutlookValidate({
        body,
      });
    },
    ...options,
  });
};

export const useOutlookAuthValidateTokenMutation = (
  options?: UseMutationOptions<
    JwtDto,
    AxiosError<IErrorApiModel>,
    OutlookAccessTokenRequest
  >
) => {
  return useMutation<
    JwtDto,
    AxiosError<IErrorApiModel>,
    OutlookAccessTokenRequest
  >({
    mutationFn: (body) => {
      return AuthService.postAuthExternalOutlookValidateToken({
        body,
      });
    },
    ...options,
  });
};
